import { T, useT } from '@transifex/react';
import { Alert, AlertIcon, AlertText, Link, LinkText, Text, VStack } from '@vidahealth/vida-ui';
import { CircleAlert } from 'lucide-react-native';
import { useEffect, useRef, useState } from 'react';

import { HoldTightScreen } from '@/components/HoldTightScreen';
import { trackEvent, useFeatureFlagVariant } from '@/modules/analytics';
import { formatBirthdayForData, formatBirthdayForDisplay } from '@/modules/localization';
import {
  ELIGIBILITY_CHECK_FIELDS,
  FEATURE_FLAGS,
  type OnboardingDataDraft,
  useRulesEngine,
} from '@/modules/rulesEngine';
import {
  AlertInfoCard,
  ContactSupport,
  ControlledSelectField,
  CtaButton,
  Screen,
  ScreenHeading,
  TextField,
  formatInteger,
  formatZipCode,
  type SelectOption,
} from '@/modules/ui';

import { MismatchExamplesSheet } from './MismatchExamplesSheet';

// stylos's ResubmitForm hides the "Optional:" wording for these two
// patterns because EMPID is part of the required ID set, not a bonus field
// to help locate the user.
const ALL_REQUIRED_PATTERNS = new Set<string>([
  ELIGIBILITY_CHECK_FIELDS.FN_LN_DOB_EMPID,
  ELIGIBILITY_CHECK_FIELDS.FN_LN_DOB_EMPID_EMAIL_GENDER,
]);

// Single-char gender enum the backend expects (Sparta's CustomerProfile);
// eligibility_check matches against M / F / O, never free text.
const GENDER_OPTIONS: SelectOption[] = [
  { value: 'M', label: 'Male' },
  { value: 'F', label: 'Female' },
  { value: 'O', label: 'Other' },
];

export function EmployeeSearchScreen() {
  const t = useT();
  const { draft, setDraft, selectedOrg, runEligibilityCheck, eligibilityCheckResult } =
    useRulesEngine();

  // in-year-revenue experiment: the test arm pushes the mismatch guidance at the
  // member (auto-open + remedy-first sheet copy); control keeps the pull-only
  // link and the original copy. An unresolved read is not 'test', so the screen
  // behaves as control until the flag lands.
  const { value: inYearRevenueVariant } = useFeatureFlagVariant<'control' | 'test'>(
    FEATURE_FLAGS.IN_YEAR_REVENUE_ONBOARDING
  );
  const pushesMismatchGuidance = inYearRevenueVariant === 'test';

  const [mismatchOpen, setMismatchOpen] = useState(false);
  // Landing on this screen already means the eligibility check failed, so the
  // mismatch guidance is pushed at the member instead of waiting behind the
  // link. Latched to once per mount: a failed Try Again surfaces the inline
  // alert below and shouldn't shove the sheet back over the form they're editing.
  const hasAutoOpenedRef = useRef(false);

  // Derive optional-field visibility from the org's eligibility_check_fields,
  // matching stylos's ResubmitForm visibility rules. Tokens beyond FN/LN/DOB
  // (the required identity inputs above) become per-org supplementary fields.
  const eligibilityFields = selectedOrg?.eligibilityCheckFields ?? '';
  const tokens = new Set(eligibilityFields ? eligibilityFields.split(/_OR_|_AND_|_/) : []);
  const hasEmpid = tokens.has('EMPID');
  const hasZip = tokens.has('ZC');
  const hasGender = tokens.has('GENDER');
  const showOptionalWording = (hasEmpid || hasZip) && !ALL_REQUIRED_PATTERNS.has(eligibilityFields);

  const empidLabel = selectedOrg?.employeeIdDisplayName ?? t('Employee ID');
  const empidPlaceholder = showOptionalWording ? `${empidLabel} (${t('Optional')})` : empidLabel;

  // Bind fields directly to the rules-engine draft. setDraft is the only
  // source of truth — local component state was removed to match the pattern
  // used by InviteCodeForm / EmployeeZipVerificationScreen.
  const firstName = draft.firstName ?? '';
  const lastName = draft.lastName ?? '';
  const dob = draft.birthday ?? '';
  const gender = draft.gender ?? '';
  const zip = draft.zipCode ?? '';
  const empid = draft.employeeId ?? '';

  // Patch one or more fields onto the rules-engine draft. Accepting an object
  // (instead of `field, value`) lets callers update derived pairs atomically —
  // e.g. the DOB input writes both the display string (`birthday`) and the
  // server format (`dateOfBirth`) in a single setDraft call.
  const setDraftField = (patch: Partial<OnboardingDataDraft>) => {
    setDraft((prev) => ({ ...prev, ...patch }));
  };

  const isChecking = eligibilityCheckResult?.isChecking === true;
  const isTryAgainDisabled = !firstName.trim() || !lastName.trim() || !dob.trim() || isChecking;

  // Mirror Sparta: the "No matching records" alert is shown only after a
  // deliberate Try Again has failed (eligibilityCheckResult.isRetry === true).
  // The initial failure that lands the user here came from a different
  // screen with isRetry=false, so the alert stays hidden on first arrival.
  const showFormError =
    eligibilityCheckResult?.passed === false && eligibilityCheckResult?.isRetry === true;

  // RTE field-set, matching Sparta's `is_rte` flag on the employee-search event.
  // (Sparta also flags FN_LN_DOB_EMPID_EMAIL_GENDER, but that's the Amwell RTE
  // set, which we don't support — so the ZIP set is the only RTE case here.)
  const isRte = eligibilityFields === ELIGIBILITY_CHECK_FIELDS.FN_LN_DOB_AND_EMPID_OR_ZC;

  const onTryAgain = () => {
    // Mirror Sparta's EmployeeSearchContainer: record the retry press with the
    // org + field-set context (no PII). `showFormError` reflects whether the
    // error was already visible before this attempt.
    trackEvent('employee-search-click-continue', {
      partnerUuid: selectedOrg?.uuid,
      eligibilityCheckFields: eligibilityFields,
      isRte,
      showFormError,
    });
    runEligibilityCheck({ isRetry: true });
  };

  useEffect(() => {
    // `selectedOrg` can still be hydrating from `?org=` on a deep link/refresh
    // when eligibilityCheckResult.passed is already false — without this guard
    // the event fires with partnerUuid/eligibilityCheckFields empty and the
    // latch makes that permanent, since it never gets a second chance to fire
    // with the real values once selectedOrg lands.
    // The flag is read on this same render, so a still-resolving variant leaves
    // `pushesMismatchGuidance` false and the effect re-runs once it lands —
    // without latching, so the test arm still gets its auto-open.
    if (
      hasAutoOpenedRef.current ||
      !pushesMismatchGuidance ||
      !selectedOrg?.uuid ||
      eligibilityCheckResult?.passed !== false
    ) {
      return;
    }
    hasAutoOpenedRef.current = true;
    setMismatchOpen(true);
    // Distinguishes an auto-impression from a deliberate tap on the link below
    // — the whole point of this ticket is finding out whether pushing the
    // guidance (vs. waiting for it to be pulled) moves the retry-success rate.
    trackEvent('mismatch-examples-shown', {
      trigger: 'auto',
      partnerUuid: selectedOrg?.uuid,
      eligibilityCheckFields: eligibilityFields,
    });
  }, [
    eligibilityCheckResult?.passed,
    selectedOrg?.uuid,
    eligibilityFields,
    pushesMismatchGuidance,
  ]);

  // Fire `eligibility-check-failed` once the "no matching records" error
  // surfaces (a deliberate retry has failed) — Sparta fires it from the same
  // `error.retry === true` transition.
  useEffect(() => {
    if (showFormError) {
      trackEvent('eligibility-check-failed', { partnerUuid: selectedOrg?.uuid });
    }
  }, [showFormError, selectedOrg?.uuid]);

  // The onboarding layout redirects away when currentRoute diverges from
  // pathname, and the provider hydrates `selectedOrg` from `?org=` on deep
  // links — so by the time this screen actually renders, selectedOrg should
  // be set. The null guard covers the brief window before that hydration
  // settles.
  if (!selectedOrg) {
    return null;
  }

  // While the real-time (RTE) check is running, show Hold Tight on this route
  // rather than making it its own onboarding step. RTE = the ZIP field-set;
  // Try Again re-runs the check, so this covers retry too.
  if (isChecking && isRte) {
    return <HoldTightScreen />;
  }

  return (
    <>
      <Screen
        footer={
          <CtaButton
            isBusy={isChecking}
            isDisabled={isTryAgainDisabled}
            onPress={onTryAgain}
            testID='employee-search-try-again'
          >
            <T _str='Try Again' />
          </CtaButton>
        }
      >
        <VStack space='2xl'>
          <ScreenHeading title={t("We're having trouble finding your information")} />

          <Text size='md' className='inline-block text-typography-900'>
            <T
              _str="This can happen if the information you entered doesn't match what's on file with your organization or health plan. {link}."
              link={
                <Link
                  onPress={() => {
                    setMismatchOpen(true);
                    trackEvent('mismatch-examples-shown', {
                      trigger: 'link',
                      partnerUuid: selectedOrg?.uuid,
                      eligibilityCheckFields: eligibilityFields,
                    });
                  }}
                  className='inline'
                  testID='employee-search-mismatch-examples'
                >
                  <LinkText size='md' className='no-underline'>
                    <T _str='See common mismatch examples' />
                  </LinkText>
                </Link>
              }
            />
          </Text>

          {showFormError ? (
            <Alert action='error' variant='solid' className='items-start py-6 pl-5 pr-6'>
              <AlertIcon as={CircleAlert} className='m-1' />
              <AlertText size='md' className='flex-1 pl-1 text-typography-900'>
                <T _str='No matching records found. Please try again or call us at (833) 670-5552.' />
              </AlertText>
            </Alert>
          ) : null}

          <TextField
            value={firstName}
            onChangeText={(text) => setDraftField({ firstName: text })}
            placeholder={t('Legal first name')}
            autoComplete='given-name'
            textContentType='givenName'
            testID='employee-search-first-name'
          />
          <TextField
            value={lastName}
            onChangeText={(text) => setDraftField({ lastName: text })}
            placeholder={t('Legal last name')}
            autoComplete='family-name'
            textContentType='familyName'
            testID='employee-search-last-name'
          />
          <TextField
            value={dob}
            onChangeText={(text) =>
              setDraftField({ birthday: text, dateOfBirth: formatBirthdayForData(text) })
            }
            format={formatBirthdayForDisplay}
            placeholder={t('Date of birth (MM / DD / YYYY)')}
            inputMode='numeric'
            keyboardType='number-pad'
            maxLength={14}
            testID='employee-search-dob'
          />

          {/* Gender — required field, rendered above the optional wording per
              stylos. A select over the backend's M/F/O enum; a free-text value
              would silently fail the eligibility match. */}
          {hasGender ? (
            <ControlledSelectField
              value={gender}
              onValueChange={(value) => setDraftField({ gender: value })}
              options={GENDER_OPTIONS}
              placeholder={t('Gender')}
              testID='employee-search-gender'
            />
          ) : null}

          {showOptionalWording ? (
            <Alert action='info' variant='outline' className='border-0 bg-transparent p-0'>
              <AlertText size='md' className='text-typography-900'>
                <T
                  _str='{optional} You can add the following info to help us find your file. Please make sure your Birthday above is accurate.'
                  optional={
                    <Text className='font-inter-700'>
                      <T _str='Optional:' />
                    </Text>
                  }
                />
              </AlertText>
            </Alert>
          ) : null}

          {hasZip ? (
            <TextField
              value={zip}
              onChangeText={(text) => setDraftField({ zipCode: text })}
              format={formatZipCode}
              placeholder={t('ZIP Code')}
              autoComplete='postal-code'
              inputMode='numeric'
              keyboardType='number-pad'
              maxLength={5}
              testID='employee-search-zip'
            />
          ) : null}

          {hasEmpid ? (
            <TextField
              value={empid}
              onChangeText={(text) => setDraftField({ employeeId: text })}
              format={selectedOrg.employeeIdInteger ? formatInteger : undefined}
              placeholder={empidPlaceholder}
              autoCapitalize='none'
              autoCorrect={false}
              inputMode={selectedOrg.employeeIdInteger ? 'numeric' : 'text'}
              keyboardType={selectedOrg.employeeIdInteger ? 'number-pad' : 'default'}
              autoComplete='off'
              testID='employee-search-empid'
            />
          ) : null}

          {selectedOrg.employeeIdDescription ? (
            <AlertInfoCard
              title={t('Where can I find this?')}
              description={selectedOrg.employeeIdDescription}
            />
          ) : (
            <ContactSupport variant='alert' showEmail showPhone />
          )}
        </VStack>
      </Screen>

      <MismatchExamplesSheet
        isOpen={mismatchOpen}
        onClose={() => setMismatchOpen(false)}
        leadsWithRemedy={pushesMismatchGuidance}
      />
    </>
  );
}
